/**
 * SupportSDKWrapper - SDK Wrapper for Support Ticket operations
 *
 * Provides backward-compatible interface for supportApiService using generated SDK.
 *
 * Maps legacy supportApiService methods to support SDK endpoints.
 *
 * Migration: Phase 1, Week 1 Day 1 (Low Complexity)
 * Service: supportApiService (1 endpoint)
 * Created: December 12, 2025
 */

import { BaseSDKWrapper } from './BaseSDKWrapper';
import { getSupport } from '../support/support';

export class SupportSDKWrapper extends BaseSDKWrapper {
  private supportSDK: any;

  constructor() {
    super('SupportSDK');
    this.supportSDK = getSupport();
  }

  private getField(formData: any, fieldName: string): any {
    if (formData && typeof formData.get === 'function') {
      const value = formData.get(fieldName);
      if (value !== undefined && value !== null) {
        return value;
      }
    }

    return formData?.[fieldName];
  }

  private hasFieldValue(value: any): boolean {
    return value !== undefined && value !== null;
  }

  /**
   * Create a support ticket
   *
   * @param formData - FormData containing ticket information
   * @returns Promise resolving to ticket creation result
   */
  async createTicket(formData: any): Promise<any> {
    this.logOperation('createTicket', { hasFormData: !!formData });

    // Extract form data fields for SDK request body
    const requestBody: any = {
      customer_email: this.getField(formData, 'customer_email'),
      fullname: this.getField(formData, 'fullname'),
      body: this.getField(formData, 'body'),
    };

    // Optional fields
    const orgId = this.getField(formData, 'org_id');
    if (this.hasFieldValue(orgId)) {
      requestBody.org_id = orgId;
    }

    const subject = this.getField(formData, 'subject');
    if (this.hasFieldValue(subject)) {
      requestBody.subject = subject;
    }

    const priority = this.getField(formData, 'priority');
    if (this.hasFieldValue(priority)) {
      requestBody.priority = priority;
    }

    // File attachments
    const attachment = this.getField(formData, 'attachment');
    if (this.hasFieldValue(attachment)) {
      requestBody.attachment = attachment;
    }

    const screenshot = this.getField(formData, 'screenshot');
    if (this.hasFieldValue(screenshot)) {
      requestBody.screenshot = screenshot;
    }

    return this.execute(
      () =>
        this.supportSDK.createSupportTicketSupportCreateSupportTicketPost(
          requestBody,
          {},
        ),
      'createTicket',
    );
  }
}

// Export singleton instance
export const supportSDK = new SupportSDKWrapper();
